// Step 1, Path to COD: the story in six chapters, time running down and the four lanes
// across. The steps are agreed and read-only: a change to them is made in code (Noaa,
// 25 Oct 2026). Saved changes from useAmazonBlueprint are drawn over the page's own.
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { BlueprintSide } from "../../api/hooks/useAmazonBlueprint";
import type { StoryStep } from "./blueprint";
import { CHAPTERS } from "./chapters";
import { ContractText, LimitBox, NeedsList } from "./ContractText";
import { STEP_CLAUSES } from "./contractClauses";
import { Dr3Panel } from "./Dr3Panel";
import { PartyChip } from "./PartyChip";
import { formatDate } from "./dates";
import { RoofProcess } from "./RoofProcess";
import { DR3_PARTS, LANES, LIMITS, type Owner } from "./pathSteps";
import styles from "./amazonOverview.module.css";

const OWNER_CLASS: Record<Owner, string> = {
  inrange: styles.boxInrange,
  amazon: styles.boxAmazon,
  third: styles.boxThird,
  both: styles.boxBoth,
};
const PANE_CLASS: Record<Owner, string> = {
  inrange: styles.paneInrange,
  amazon: styles.paneAmazon,
  third: styles.paneThird,
  both: styles.paneBoth,
};
const PART_CLASS: Record<Owner, string> = {
  inrange: styles.partInrange,
  amazon: styles.partAmazon,
  third: styles.partThird,
  both: styles.partBoth,
};

const sideName = (by: BlueprintSide | null): string =>
  by === "amazon" ? "Amazon" : "InRange";
const onDay = (iso: string | null): string => (iso ? ` on ${formatDate(new Date(iso))}` : "");

type Pane = { kind: "step"; id: string } | { kind: "needs" } | { kind: "none" };

type StoryView = { chapter: number; present: boolean; pane: Pane };

const FIRST_VIEW: StoryView = {
  chapter: 0,
  present: false,
  pane: { kind: "none" },
};

/** The view lives in its history entry, so Back returns to the chapter and pane last shown. */
const readView = (state: unknown): StoryView => ({
  ...FIRST_VIEW,
  ...((state as { story?: Partial<StoryView> } | null)?.story ?? {}),
});

const Title = ({ step }: { step: StoryStep }) => (
  <>
    {step.title}
    {step.unapproved && <span className={styles.star}>*</span>}
  </>
);

const toggledNumber = (list: number[], n: number): number[] =>
  list.includes(n) ? list.filter((x) => x !== n) : [...list, n].sort();

export const StoryMap = ({
  steps,
  blueprintError,
}: {
  steps: StoryStep[];
  /** The saved changes could not be loaded: the page shows its own steps only. */
  blueprintError: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { chapter, present, pane } = readView(location.state);
  const show = useCallback(
    (patch: Partial<StoryView>) => {
      const now = readView(location.state);
      const to = { ...now, ...patch };
      if (JSON.stringify(to) === JSON.stringify(now)) return;
      navigate(
        { search: location.search, hash: location.hash },
        { state: { story: to }, preventScrollReset: true },
      );
    },
    [navigate, location],
  );
  const setPane = useCallback((p: Pane) => show({ pane: p }), [show]);
  // The needs list's chapter filter; none ticked shows every chapter.
  const [needsChapters, setNeedsChapters] = useState<number[]>([]);

  const byId = useMemo(() => new Map(steps.map((s) => [s.id, s])), [steps]);
  // Site & Build first, Legal last.
  const lanes = [...LANES].reverse();
  const visible = steps.filter((s) => !s.hidden && !s.inBox && !s.offMap);

  // Boxes drawn level with a box in another lane: pushed down after layout, since how far
  // depends on how tall the boxes above them wrap.
  const boxEls = useRef(new Map<string, HTMLElement>());
  const gridRef = useRef<HTMLDivElement | null>(null);
  const levelled = visible.filter((s) => {
    const to = s.mapLevel && byId.get(s.mapLevel.with);
    return to && !to.hidden && to.chapter === s.chapter && to.lane !== s.lane;
  });
  useLayoutEffect(() => {
    const align = () => {
      // Every box, so one whose target was hidden or moved since loses its old gap.
      boxEls.current.forEach((el) => {
        el.style.marginTop = "";
      });
      for (const s of levelled) {
        const el = boxEls.current.get(s.id);
        const level = s.mapLevel;
        const target = level && boxEls.current.get(level.with);
        if (!el || !level || !target) continue;
        const t = target.getBoundingClientRect();
        const want = level.at === "top" ? t.top : t.top + (t.height - el.offsetHeight) / 2;
        const gap = want - el.getBoundingClientRect().top;
        if (gap > 0) el.style.marginTop = `${gap}px`;
      }
    };
    align();
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(align);
    });
    if (gridRef.current) observer.observe(gridRef.current);
    // A target can change height without the grid changing size.
    levelled.forEach((s) => {
      const target = s.mapLevel && boxEls.current.get(s.mapLevel.with);
      if (target) observer.observe(target);
    });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  });

  const shownAs = (id: string): string => byId.get(id)?.inBox ?? id;
  // A removed step reached through Back shows as nothing selected.
  const live = (id: string) => {
    const s = byId.get(id);
    return s && !s.hidden ? s : undefined;
  };
  const selected = pane.kind === "step" ? live(pane.id) : undefined;
  const waits = selected
    ? [...selected.deps, ...(selected.endDeps ?? [])].map(shownAs)
    : [];
  const unlocksOf = (id: string) =>
    steps.filter(
      (s) =>
        !s.hidden && (s.deps.includes(id) || (s.endDeps ?? []).includes(id)),
    );
  const unlocks = selected ? unlocksOf(selected.id).map((s) => shownAs(s.id)) : [];

  const move = (to: number) =>
    show({ chapter: Math.max(0, Math.min(CHAPTERS.length - 1, to)) });

  useEffect(() => {
    if (!present) return;
    const key = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("input, select, textarea")) return;
      // Alt/Cmd+arrow is the browser's own Back and Forward.
      if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (e.key === "Escape") show({ present: false });
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        show({ chapter: Math.min(CHAPTERS.length - 1, chapter + 1) });
      }
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        show({ chapter: Math.max(0, chapter - 1) });
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [present, chapter, show]);

  const fade = (i: number): string =>
    present ? (i > chapter ? styles.ghost : i < chapter ? styles.dim : "") : "";

  /** The steps drawn as parts of ``s``'s box, each with what it needs from Amazon. */
  const partsOf = (s: StoryStep) =>
    steps.filter((t) => t.inBox === s.id && !t.hidden);
  /** How many things the box needs from Amazon, its parts' included. */
  const needsCount = (s: StoryStep) =>
    s.needs.length + partsOf(s).reduce((sum, t) => sum + t.needs.length, 0);

  const box = (s: StoryStep) => {
    const relation =
      selected?.id === s.id
        ? null
        : waits.includes(s.id)
          ? "Waits on"
          : unlocks.includes(s.id)
            ? "Unlocks"
            : null;
    const limit = LIMITS[s.id];
    const classes = [
      styles.storyBox,
      OWNER_CLASS[s.owner],
      s.proposed ? styles.boxProposed : "",
      selected?.id === s.id ? styles.boxSelected : "",
    ].join(" ");
    return (
      <button
        key={s.id}
        ref={(el) => {
          if (el) boxEls.current.set(s.id, el);
          else boxEls.current.delete(s.id);
        }}
        type="button"
        className={classes}
        onClick={() => setPane({ kind: "step", id: s.id })}
      >
        {relation && (
          <span
            className={relation === "Waits on" ? styles.relWaits : styles.relUnlocks}
          >
            {relation}
          </span>
        )}
        {needsCount(s) > 0 && (
          <span
            className={styles.needsDot}
            aria-label={`Needs ${needsCount(s)} ${needsCount(s) === 1 ? "thing" : "things"} from Amazon`}
          >
            {needsCount(s)}
          </span>
        )}
        {(s.proposed || s.custom) && (
          <span className={s.by === "amazon" ? styles.tagAmazon : styles.tag}>
            {s.proposed ? "Proposed" : "Added"} by {sideName(s.by)}
          </span>
        )}
        <span className={styles.boxTitle}>
          <Title step={s} />
        </span>
        {s.desc && <span className={styles.boxDesc}>{s.desc}</span>}
        {s.halves && (
          <span className={styles.parts}>
            {s.halves.map(([id, label]) => (
              <span
                key={id}
                className={`${styles.part} ${PART_CLASS[byId.get(id)?.owner ?? "inrange"]}`}
              >
                {label}
              </span>
            ))}
          </span>
        )}
        {s.reviews && (
          <span className={styles.parts}>
            {DR3_PARTS.map((p) => (
              <span
                key={p.label}
                className={`${styles.part} ${PART_CLASS[p.owner]}`}
              >
                {p.label}
              </span>
            ))}
          </span>
        )}
        {limit && !limit.unbounded && (
          <span className={limit.risk ? styles.limRisk : styles.lim}>{limit.label}</span>
        )}
      </button>
    );
  };

  const stepPane = (s: StoryStep) => {
    const lane = LANES.find((l) => l.id === s.lane)?.name ?? "";
    const title = (id: string) => byId.get(id)?.title ?? id;
    const unlockTitles = [
      ...new Set(unlocksOf(s.id).map((u) => title(shownAs(u.id)))),
    ].filter((t) => t !== s.title);
    const limit = LIMITS[s.id];
    return (
      <>
        {s.id === "roof" && (
          <div className={styles.paneWide}>
            <RoofProcess />
          </div>
        )}
        <div className={styles.paneCol}>
          <div className={styles.eyebrow}>
            Chapter {s.chapter + 1} · {CHAPTERS[s.chapter].title} · {lane}
          </div>
          <h3>
            <Title step={s} />
          </h3>
          <div>
            <PartyChip owner={s.owner} label={s.ownerLabel} />{" "}
            {(s.proposed || s.custom) && (
              <span className={styles.badge}>
                {s.proposed
                  ? `Proposed by ${sideName(s.by)} · not yet agreed`
                  : `Added by ${sideName(s.by)} · agreed`}
              </span>
            )}
          </div>
          {(s.suggestedBy || s.agreedBy) && (
            <p className={styles.sub}>
              {[
                s.suggestedBy && `Suggested by ${s.suggestedBy}${onDay(s.suggestedAt)}`,
                s.agreedBy && `Agreed by ${s.agreedBy}${onDay(s.agreedAt)}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {s.subs.length ? (
            <ul className={styles.subs}>
              {s.subs.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : (
            <p className={styles.sub}>{s.desc}</p>
          )}
          {s.unapproved && (
            <p className={styles.sub}>
              <span className={styles.star}>*</span> The process for this step
              is not agreed yet.
            </p>
          )}
        </div>
        <div className={styles.paneCol}>
          {limit && <LimitBox limit={limit} />}
          {s.needs.length > 0 && (
            <div className={styles.needs}>
              <div className={styles.needsTitle}>
                <span className={styles.dotStatic} /> Needs from Amazon, before
                this step can start
              </div>
              <NeedsList step={s} />
            </div>
          )}
          {partsOf(s)
            .filter((t) => t.needs.length > 0)
            .map((t) => (
              <div key={t.id} className={styles.needs}>
                <div className={styles.needsTitle}>
                  <span className={styles.dotStatic} /> {t.title}: needs from
                  Amazon
                </div>
                <NeedsList step={t} />
              </div>
            ))}
          <dl className={styles.facts}>
            <dt>Waits on</dt>
            <dd>
              {waits.filter((w) => w !== s.id).length
                ? [...new Set(waits.filter((w) => w !== s.id).map(title))].join(", ")
                : "Nothing"}
            </dd>
            <dt>Unlocks</dt>
            <dd>{unlockTitles.length ? unlockTitles.join(", ") : "-"}</dd>
            {s.source && (
              <>
                <dt>Contract</dt>
                <dd>
                  {s.source}{" "}
                  <ContractText ids={STEP_CLAUSES[s.id] ?? []} more="see more" />
                </dd>
              </>
            )}
          </dl>
        </div>
        {s.reviews && (
          <div className={styles.paneWide}>
            <Dr3Panel siteCode={null} submitted={null} />
          </div>
        )}
      </>
    );
  };

  const paneBody = () => {
    if (pane.kind === "needs") {
      return (
        <div className={styles.paneWide}>
          <div className={styles.eyebrow}>Amazon design inputs</div>
          <h3>Everything we need from Amazon</h3>
          <div
            className={styles.chapterFilter}
            role="group"
            aria-label="Filter by chapter"
          >
            <span>Chapter</span>
            {CHAPTERS.map((c, i) => (
              <button
                key={c.title}
                type="button"
                title={c.title}
                aria-pressed={needsChapters.includes(i)}
                onClick={() => setNeedsChapters((on) => toggledNumber(on, i))}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <div className={styles.needsGrid}>
            {steps
              .filter(
                (s) =>
                  s.needs.length &&
                  !s.hidden &&
                  (!needsChapters.length || needsChapters.includes(s.chapter)),
              )
              .map((s) => (
                <div key={s.id} className={styles.needsCard}>
                  <b>
                    <Title step={s} />
                  </b>
                  <span className={styles.source}>
                    Chapter {s.chapter + 1} ·{" "}
                    {LANES.find((l) => l.id === s.lane)?.name}
                  </span>
                  <NeedsList step={s} />
                </div>
              ))}
          </div>
        </div>
      );
    }
    if (selected) return stepPane(selected);
    return (
      <div className={styles.paneWide}>
        <h3>Select a step to see its detail</h3>
        <p className={styles.sub}>
          Each box on the map is one step on the path to COD. Click one to see
          what it involves, who owns it, what it waits on and what it unlocks.
        </p>
        <p className={styles.sub}>
          Use See all in the key above for everything we need from Amazon.
        </p>
      </div>
    );
  };

  const c = CHAPTERS[chapter];
  return (
    <div className={styles.story}>
      <div className={styles.storyPanel}>
        <div className={styles.storyText} title={c.story}>
          Chapter {chapter + 1} of {CHAPTERS.length} ·{" "}
          <b>{c.title}</b> · {c.line}
        </div>
        <div className={styles.acts}>
          <button
            type="button"
            className={styles.button}
            disabled={chapter === 0}
            onClick={() => move(chapter - 1)}
          >
            Previous chapter
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={chapter === CHAPTERS.length - 1}
            onClick={() => move(chapter + 1)}
          >
            Next chapter
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={() =>
              show(present ? { present: false } : { present: true, chapter: 0 })
            }
          >
            {present ? "Show all chapters" : "Present the story"}
          </button>
        </div>
      </div>

      {blueprintError && (
        <div className={styles.controls}>
          <span className={styles.badgeError}>
            Could not load the saved changes to the steps: showing the page's own
            steps
          </span>
        </div>
      )}

      <div className={styles.legend}>
        <PartyChip owner="inrange" />
        <PartyChip owner="amazon" />
        <PartyChip owner="third" />
        <PartyChip owner="both" />
        <span>
          <span className={styles.dotStatic} /> Needs data from Amazon{" "}
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setPane({ kind: "needs" })}
          >
            See all
          </button>
        </span>
        {visible.some((s) => s.proposed) && (
          <span>
            <i className={styles.swatchProposed} /> Proposed, not yet agreed
          </span>
        )}
        <span>
          <span className={styles.star}>*</span> Process not approved
        </span>
      </div>

      <div className={styles.storyBody}>
        <div className={styles.storyMain}>
          <div className={styles.storyScroll}>
            <div className={styles.storyGrid} ref={gridRef}>
              <div className={styles.corner}>Time ↓</div>
              {lanes.map((l) => (
                <div key={l.id} className={styles.laneCell}>
                  {l.name}
                  <small>{l.sub}</small>
                </div>
              ))}
              {CHAPTERS.map((ch, i) => {
                return (
                  <Fragment key={ch.title}>
                    <div
                      className={[
                        styles.chapterHead,
                        i === chapter ? styles.chapterCurrent : "",
                        i === 0 ? styles.chapterFirst : "",
                        i === CHAPTERS.length - 1 ? styles.chapterLast : "",
                        fade(i),
                      ].join(" ")}
                    >
                      <button
                        type="button"
                        className={styles.chapterButton}
                        onClick={() => move(i)}
                      >
                        <span className={styles.chapterName}>
                          <b>{i + 1}</b>
                          <span>{ch.title}</span>
                        </span>
                        <span className={styles.chapterLine}>{ch.line}</span>
                      </button>
                    </div>
                    {lanes.map((l) => (
                      <div
                        key={l.id}
                        className={[
                          styles.storyCell,
                          fade(i),
                        ].join(" ")}
                      >
                        {visible
                          .filter((s) => s.lane === l.id && s.chapter === i)
                          .map(box)}
                      </div>
                    ))}
                  </Fragment>
                );
              })}
            </div>
          </div>

        </div>
        <section
          className={[
            styles.pane,
            selected ? PANE_CLASS[selected.owner] : "",
          ].join(" ")}
          aria-live="polite"
        >
          {paneBody()}
        </section>
      </div>
    </div>
  );
};
